/**
 * @description 从 CasualGenreProfile 页面跳转到 MobileGames 的 URL 构造工具
 */

import moment from 'moment';
import { history } from 'umi';
import { generateMetricKey } from '@intelligence/v3/utils/metric';
import { SortMode, SortType } from '@home/enums/common';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';

import type { MainGenre, SubGenre } from '@intelligence/components/GenreSelectMiddleware/types';

/**
 * 品类参数序列化时使用的分隔符（与 PCConsoleGames 页面保持一致）
 */
const GENRE_JOINER = '$$';

/**
 * 序列化主品类值为 URL 参数格式
 * @param mainGenre - 主品类对象
 * @returns 序列化后的字符串，格式: "relation$$value"
 *
 * @example
 * serializeMainGenre({ relation: 'or', value: 'RPG' }) => "or$$RPG"
 */
const serializeMainGenre = (mainGenre: MainGenre): string => {
  const relation = mainGenre?.relation || 'or';
  const value = mainGenre?.value || '';
  return !value ? '' : `${relation}${GENRE_JOINER}${value}`;
};

/**
 * 序列化子品类值为 URL 参数格式
 * @param subGenre - 子品类对象
 * @returns 序列化后的字符串，格式: "parentValue$$value"
 *
 * @example
 * serializeSubGenre({ parentValue: 'RPG', value: 'ActionRPG' }) => "RPG$$ActionRPG"
 */
const serializeSubGenre = (subGenre: SubGenre): string => {
  const parentValue = subGenre?.parentValue || '';
  const value = subGenre?.value || '';
  return !parentValue || !value ? '' : `${parentValue}${GENRE_JOINER}${value}`;
};

/**
 * 序列化 App Magic 主品类（或超级品类）
 * @param genre - 品类值
 * @returns 序列化后的字符串
 */
const serializeAppMagicMainGenre = (genre: string): string => {
  return `${Relation.Or}${GENRE_JOINER}${genre}`;
};

/**
 * 序列化 App Magic 子品类（使用多级路径）
 * @param genrePath - 品类路径
 * @returns 序列化后的字符串
 */
const serializeAppMagicSubGenre = (...genrePath: string[]): string => {
  return genrePath.join(GENRE_JOINER);
};

// App Magic 各层级品类序列化函数别名
const serializeSuperGenre = serializeAppMagicMainGenre;
const serializeMajorCategory = serializeAppMagicSubGenre;
const serializeMidCategory = serializeAppMagicSubGenre;
const serializeSubCategory = serializeAppMagicSubGenre;

/**
 * 序列化品类参数为 URL 查询参数格式
 * @param genreParams - 品类参数对象
 * @returns URL 查询参数对象 { genre_source, genre_main_genres, genre_sub_genres }
 *
 * @example
 * serializeGenreParams({
 *   source: 'appmagic',
 *   mainGenres: [{ relation: 'or', value: 'RPG' }],
 *   subGenres: [{ parentValue: 'RPG', value: 'ActionRPG' }]
 * }) => {
 *   genre_source: 'appmagic',
 *   genre_main_genres: 'or$$RPG',
 *   genre_sub_genres: 'RPG$$ActionRPG'
 * }
 */
export const serializeGenreParams = (genreParams?: IGenreInformation & { source: string }): IRecord => {
  const result: IRecord = {};

  if (!genreParams) {
    return result;
  }

  const { source } = genreParams || {};

  // 序列化 genre_source
  if (source) {
    result.genre_source = source;
  }

  switch (source) {
    case GENRE_SOURCE_MAP.SENSORTOWER: {
      const { sensortower_genre: sensortowerGenre, sensortower_sub_genre: sensortowerSubGenre } = genreParams || {};
      // 只有当前层级品类值不为空时，才继续构建下一层级的品类url参数
      if (sensortowerGenre) {
        result.genre_main_genres = serializeMainGenre({
          relation: Relation.Or,
          value: sensortowerGenre,
        } as any);
        if (sensortowerSubGenre) {
          result.genre_sub_genres = serializeSubGenre({
            value: sensortowerSubGenre,
            parentValue: sensortowerGenre,
          } as any);
        }
      }
    }
    case GENRE_SOURCE_MAP.APP_MAGIC: {
      const {
        super_genre: superGenre,
        major_category: majorCategory,
        mid_category: midCategory,
        sub_category: subCategory,
      } = genreParams || {};
      // 只有当前层级品类值不为空时，才继续构建下一层级的品类url参数
      if (superGenre) {
        result.genre_main_genres = serializeSuperGenre(superGenre);
        if (majorCategory) {
          result.genre_sub_genres = serializeMajorCategory(superGenre, majorCategory);
          if (midCategory) {
            result.genre_level3_genres = serializeMidCategory(superGenre, majorCategory, midCategory);
            if (subCategory) {
              result.genre_level4_genres = serializeSubCategory(superGenre, majorCategory, midCategory, subCategory);
            }
          }
        }
      }
    }
  }

  return result;
};

/**
 * 列 key 与跳转配置的映射
 */
interface IColumnJumpConfig {
  /** 目标页面 Tab */
  targetTab: 'ActiveUsers' | 'Downloads';
  /** 排序指标 */
  orderMetric: string;
  /** 排序类型：0=按值，1=环比 */
  orderMode: SortMode;
  /** 排序方式 */
  order: SortType;
  /** 是否需要 Range 筛选器 */
  needsRangeFilter: boolean;
  /** Range 筛选器类型：'au' | 'download' */
  rangeFilterType?: 'au' | 'download';
  /** 日粒度阈值（DAU/Downloads日粒度使用） */
  dailyThreshold?: number;
  /** 月粒度阈值（MAU/Downloads月粒度使用） */
  monthlyThreshold?: number;
  /** 是否需要将阈值乘以天数差/月数差（仅 Downloads 需要乘，AU 是固定值） */
  multiplyByDateSpan?: boolean;
  /** 要求的日期粒度（DAU只能用daily，MAU只能用monthly） */
  requiredGranularity?: IGranularity;
}

/**
 * 列 key 到跳转配置的映射
 *
 * 注意：
 * - DAU 指标只在日粒度下存在，只配置 dailyThreshold，requiredGranularity = 'daily'
 * - MAU 指标只在月粒度下存在，只配置 monthlyThreshold，requiredGranularity = 'monthly'
 * - Downloads 指标在日/月粒度都存在，需要同时配置两个阈值
 */
const COLUMN_JUMP_CONFIG_MAP: Record<string, IColumnJumpConfig> = {
  // Top Games 系列
  topGamesByDownloads: {
    targetTab: 'ActiveUsers',
    orderMetric: 'download',
    orderMode: SortMode.Number,
    order: SortType.Desc,
    needsRangeFilter: false,
  },
  topGamesByDAU: {
    targetTab: 'ActiveUsers',
    orderMetric: 'dau',
    orderMode: SortMode.Number,
    order: SortType.Desc,
    needsRangeFilter: false,
    requiredGranularity: 'daily',
  },
  topGamesByMAU: {
    targetTab: 'ActiveUsers',
    orderMetric: 'mau',
    orderMode: SortMode.Number,
    order: SortType.Desc,
    needsRangeFilter: false,
    requiredGranularity: 'monthly',
  },
  // Rising Games 系列
  risingGamesByDownloads: {
    targetTab: 'ActiveUsers',
    orderMetric: 'download',
    orderMode: SortMode.Ratio,
    order: SortType.Desc,
    needsRangeFilter: true,
    rangeFilterType: 'download',
    dailyThreshold: 50000,
    monthlyThreshold: 500000,
    multiplyByDateSpan: true,
  },
  risingGamesByDAU: {
    targetTab: 'ActiveUsers',
    orderMetric: 'dau',
    orderMode: SortMode.Ratio,
    order: SortType.Desc,
    needsRangeFilter: true,
    rangeFilterType: 'au',
    dailyThreshold: 500000,
    multiplyByDateSpan: false,
    requiredGranularity: 'daily',
  },
  risingGamesByMAU: {
    targetTab: 'ActiveUsers',
    orderMetric: 'mau',
    orderMode: SortMode.Ratio,
    order: SortType.Desc,
    needsRangeFilter: true,
    rangeFilterType: 'au',
    monthlyThreshold: 1000000,
    multiplyByDateSpan: false,
    requiredGranularity: 'monthly',
  },
};

/**
 * 计算范围筛选器阈值
 * @param config - 跳转配置
 * @param granularity - 日期粒度
 * @param dateRange - 日期范围
 * @returns 计算后的阈值
 */
const calculateRangeThreshold = (
  config: IColumnJumpConfig,
  granularity: IGranularity,
  dateRange: [moment.Moment, moment.Moment],
): number => {
  const [startDate, endDate] = dateRange || [];
  const isDaily = granularity === 'daily';
  const threshold = isDaily ? config.dailyThreshold || 0 : config.monthlyThreshold || 0;

  if (!config.multiplyByDateSpan) {
    return threshold;
  }

  const diff = isDaily ? endDate.diff(startDate, 'days') : endDate.diff(startDate, 'months');
  return diff * threshold;
};

/**
 * 构造跳转 URL 参数
 * @param columnKey - 列 key
 * @param dataSource - 数据源（默认 'sensortower'）
 * @param granularity - 日期粒度
 * @param dateRange - 日期范围 [startDate, endDate]
 * @returns URL query 参数对象
 */
export const buildTopGamesMetricFilter = (
  columnKey: string,
  dataSource: string,
  granularity: IGranularity,
  dateRange: [moment.Moment, moment.Moment],
): IMetricFilter[] => {
  const config = COLUMN_JUMP_CONFIG_MAP[columnKey];
  if (!config) {
    console.warn(`[buildJumperUrlParams] 未找到列 key "${columnKey}" 的跳转配置`);
    return [];
  }

  if (!config.needsRangeFilter || !config.rangeFilterType) {
    return [];
  }

  const rangeThreshold = calculateRangeThreshold(config, granularity, dateRange);

  const auMetricKeyMap: IRecord<string> = {
    daily: 'avg_dau',
    weekly: 'avg_wau',
    monthly: 'avg_mau',
  };

  if (config.rangeFilterType === 'au') {
    const auMetric = auMetricKeyMap[granularity];
    const metric = generateMetricKey({ dataSource, metric: auMetric });
    return [{ metric, opt: '>=', value: rangeThreshold }];
  }

  if (config.rangeFilterType === 'download') {
    const metric = generateMetricKey({ dataSource, metric: 'download' });
    return [{ metric, opt: '>=', value: rangeThreshold }];
  }

  return [];
};

/**
 * 添加数组类型的筛选器参数
 * @param paramKey - URL 参数键
 * @param filterKey - 筛选器键
 * @param params - 目标参数对象
 * @param filterValue - 筛选器值对象
 */
const addArrayParam = (paramKey: string, filterKey: string, params: IRecord, filterValue?: IRecord): void => {
  const value = filterValue?.[filterKey]?.value || [];
  if (value.length > 0) {
    params[paramKey] = value.join(',');
  }
};

/**
 * 构造跳转 URL 参数
 * @param columnKey - 列 key
 * @param dataSource - 数据源（默认 'sensortower'）
 * @param granularity - 日期粒度
 * @param dateRange - 日期范围 [startDate, endDate]
 * @param genreParams - 品类参数（可选）
 * @param filterValue - 筛选器值，用于获取地区国家筛选器的值（可选）
 * @returns URL query 参数对象
 */
export const buildJumperUrlParams = (
  columnKey: string,
  dataSource: string,
  granularity: IGranularity,
  dateRange: [moment.Moment, moment.Moment],
  genreParams?: IGenreInformation & { source: string },
  filterValue?: IRecord,
): IRecord => {
  const config = COLUMN_JUMP_CONFIG_MAP[columnKey];
  if (!config) {
    console.warn(`[buildJumperUrlParams] 未找到列 key "${columnKey}" 的跳转配置`);
    return {};
  }

  const actualDataSource = genreParams?.source || dataSource;

  const params: IRecord = {
    use_url_filter: 1,
    granularity,
    start_date: dateRange?.[0]?.format('YYYY-MM-DD') || '',
    end_date: dateRange?.[1]?.format('YYYY-MM-DD') || '',
    order_source: actualDataSource,
    order_metric: config.orderMetric,
    order_mode: config.orderMode,
    order: config.order === SortType.Asc ? 'asc' : 'desc',
    source: actualDataSource,
  };

  // 处理 Range 筛选器
  if (config.needsRangeFilter && config.rangeFilterType) {
    const rangeThreshold = calculateRangeThreshold(config, granularity, dateRange);

    if (config.rangeFilterType === 'au') {
      params.au_range_min = rangeThreshold;
    } else if (config.rangeFilterType === 'download') {
      params.downloads_range_min = rangeThreshold;
    }
  }

  // 添加品类参数
  Object.assign(params, serializeGenreParams(genreParams));

  // 添加地区国家参数
  const regionValue = filterValue?.[filterKeyMap.REGION];
  const markets = regionValue?.markets || [];
  if (markets.length > 0) {
    params.region = markets.join(',');
  }

  // 添加数组类型筛选器参数
  addArrayParam('platform', filterKeyMap.PLATFORM, params, filterValue);
  addArrayParam('company', filterKeyMap.ALL_COMPANYS, params, filterValue);
  addArrayParam('product_model', filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL, params, filterValue);
  addArrayParam('appmagic_themes', filterKeyMap.APPMAGIC_TAG_THEMES, params, filterValue);
  addArrayParam('appmagic_features', filterKeyMap.APPMAGIC_TAG_FEATURES, params, filterValue);
  addArrayParam('appmagic_ip', filterKeyMap.APPMAGIC_TAG_IP, params, filterValue);
  addArrayParam('appmagic_settings', filterKeyMap.APPMAGIC_TAG_SETTINGS, params, filterValue);
  addArrayParam('appmagic_art_styles', filterKeyMap.APPMAGIC_TAG_ART_STYLES, params, filterValue);

  // 添加 Release Time 筛选器参数
  const releaseTimeValue = filterValue?.[filterKeyMap.RELEASE_TIME]?.value || [];
  if (releaseTimeValue.length === 2 && (releaseTimeValue[0] || releaseTimeValue[1])) {
    if (releaseTimeValue[0]) {
      params.release_start_date = moment(releaseTimeValue[0]).format('YYYY-MM-DD');
    }
    if (releaseTimeValue[1]) {
      params.release_end_date = moment(releaseTimeValue[1]).format('YYYY-MM-DD');
    }
  }

  return params;
};

/**
 * 跳转到 MobileGames 页面
 * @param columnKey - 列 key
 * @param dataSource - 数据源（默认 'sensortower'）
 * @param granularity - 日期粒度
 * @param dateRange - 日期范围 [startDate, endDate]
 * @param genreParams - 品类参数（可选）
 * @param filterValue - 筛选器值，用于传递地区国家筛选器（可选）
 */
export const jumpToMobileGames = (
  columnKey: string,
  dataSource: string,
  granularity: IGranularity,
  dateRange: [moment.Moment, moment.Moment],
  genreParams?: IGenreInformation & { source: string },
  filterValue?: IRecord,
): void => {
  const config = COLUMN_JUMP_CONFIG_MAP[columnKey];
  if (!config) {
    console.warn(`[jumpToMobileGames] 未找到列 key "${columnKey}" 的跳转配置`);
    return;
  }

  const params = buildJumperUrlParams(columnKey, dataSource, granularity, dateRange, genreParams, filterValue);

  // 使用路由跳转，避免整页刷新
  const targetUrl = `/intelligence/topCharts/mobileGames/${config.targetTab}`;
  history.push({
    pathname: targetUrl,
    query: params,
  });
};
